import { Alert, Button, Input, message, Modal, Space, Table, Tag, Typography } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";

import { useApiContext } from "../../../utils";

import { createRecipeApi } from "./api";
import { RecipeSummary } from "./types";

const { Title } = Typography;

export const RecipeList: React.FC = () => {
  const { source } = useParams<{ source: string }>();
  const { url } = useRouteMatch();
  const history = useHistory();
  const apiContext = useApiContext();

  const [recipes, setRecipes] = useState<RecipeSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [newRecipeId, setNewRecipeId] = useState("");
  const [cloneModalVisible, setCloneModalVisible] = useState(false);
  const [cloneSourceId, setCloneSourceId] = useState("");
  const [cloneNewId, setCloneNewId] = useState("");
  const [promoteModalVisible, setPromoteModalVisible] = useState(false);
  const [promoteTarget, setPromoteTarget] = useState<RecipeSummary | null>(null);
  const [promoting, setPromoting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<RecipeSummary | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [publishingId, setPublishingId] = useState<string | null>(null);

  const api = useCallback(() => {
    if (!apiContext?.DEPRECATED_api) {
      return null;
    }
    return createRecipeApi(apiContext.DEPRECATED_api);
  }, [apiContext]);

  const loadRecipes = useCallback(async () => {
    const recipeApi = api();
    if (!recipeApi) {
      return;
    }
    setLoading(true);
    try {
      const response = await recipeApi.list();
      const filtered = (response.recipes || []).filter(r => !r.source || r.source === source);
      setRecipes(filtered);
    } catch (error: unknown) {
      const err = error as { error?: { message?: string } };
      message.error(err?.error?.message || "Failed to load recipes");
    } finally {
      setLoading(false);
    }
  }, [api, source]);

  useEffect(() => {
    loadRecipes();
  }, [loadRecipes]);

  const handleCreate = () => {
    const trimmed = newRecipeId.trim();
    if (!trimmed) {
      message.error("Recipe ID is required");
      return;
    }
    if (!/^[a-z0-9_]+$/.test(trimmed)) {
      message.error("Recipe ID must only contain lowercase letters, numbers, and underscores");
      return;
    }
    // Fix 8: Duplicate check
    if (recipes.some(r => r.recipe_id === trimmed)) {
      message.error("A recipe with this ID already exists");
      return;
    }
    setCreateModalVisible(false);
    setNewRecipeId("");
    history.push(`${url}/${trimmed}`);
  };

  // Fix 4: Clone handler
  const handleClone = () => {
    const trimmed = cloneNewId.trim();
    if (!trimmed) {
      message.error("New Recipe ID is required");
      return;
    }
    if (!/^[a-z0-9_]+$/.test(trimmed)) {
      message.error("Recipe ID must only contain lowercase letters, numbers, and underscores");
      return;
    }
    if (recipes.some(r => r.recipe_id === trimmed)) {
      message.error("A recipe with this ID already exists");
      return;
    }
    setCloneModalVisible(false);
    setCloneNewId("");
    history.push(`${url}/${trimmed}?cloneFrom=${encodeURIComponent(cloneSourceId)}`);
  };

  // Fix 2: Promote/demote handler
  const handlePromote = async () => {
    const recipeApi = api();
    if (!recipeApi || !promoteTarget) {
      return;
    }
    const newProdEnabled = !promoteTarget.prod_enabled;
    setPromoting(true);
    try {
      await recipeApi.promote(promoteTarget.recipe_id, newProdEnabled);
      message.success(
        newProdEnabled
          ? `Recipe "${promoteTarget.recipe_id}" promoted to production`
          : `Recipe "${promoteTarget.recipe_id}" demoted from production`
      );
      setPromoteModalVisible(false);
      setPromoteTarget(null);
      loadRecipes();
    } catch (error: unknown) {
      const err = error as { error?: { message?: string } };
      message.error(err?.error?.message || "Failed to update production status");
    } finally {
      setPromoting(false);
    }
  };

  const handleUnpublish = (recipe: RecipeSummary) => {
    const recipeApi = api();
    if (!recipeApi) {
      return;
    }
    Modal.confirm({
      title: "Unpublish Recipe",
      content: recipe.prod_enabled
        ? `Recipe "${recipe.recipe_id}" is live in production. This will demote it from prod and remove it from the manifest.`
        : `Remove "${recipe.recipe_id}" from the manifest? It will revert to draft status.`,
      okText: "Unpublish",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          if (recipe.prod_enabled) {
            await recipeApi.promote(recipe.recipe_id, false);
          }
          await recipeApi.unpublish(recipe.recipe_id);
          message.success(`Recipe "${recipe.recipe_id}" unpublished`);
          loadRecipes();
        } catch (error: unknown) {
          const err = error as { error?: { message?: string } };
          message.error(err?.error?.message || "Failed to unpublish");
        }
      },
    });
  };

  const handleDelete = async () => {
    const recipeApi = api();
    if (!recipeApi || !deleteTarget) {
      return;
    }
    setDeleting(true);
    try {
      await recipeApi.deleteRecipe(deleteTarget.recipe_id);
      message.success(`Recipe "${deleteTarget.recipe_id}" deleted`);
      setDeleteModalVisible(false);
      setDeleteTarget(null);
      loadRecipes();
    } catch (error: unknown) {
      const err = error as { error?: { message?: string } };
      message.error(err?.error?.message || "Failed to delete recipe");
    } finally {
      setDeleting(false);
    }
  };

  const handlePublish = (recipe: RecipeSummary) => {
    const recipeApi = api();
    if (!recipeApi || !recipe.last_run_id) {
      return;
    }
    Modal.confirm({
      title: "Publish Recipe",
      content: `Publish "${recipe.recipe_id}" using the latest successful run? This will make it available in dev/staging.`,
      okText: "Publish",
      onOk: async () => {
        setPublishingId(recipe.recipe_id);
        try {
          await recipeApi.publish(recipe.recipe_id, recipe.last_run_id!);
          message.success(`Recipe "${recipe.recipe_id}" published to dev/staging`);
          loadRecipes();
        } catch (error: unknown) {
          const err = error as { error?: { message?: string } };
          message.error(err?.error?.message || "Failed to publish");
        } finally {
          setPublishingId(null);
        }
      },
    });
  };

  const columns = [
    {
      title: "Recipe ID",
      dataIndex: "recipe_id",
      key: "recipe_id",
    },
    {
      title: "Name",
      dataIndex: "recipe_name",
      key: "recipe_name",
      render: (name: string | undefined) => name || "-",
    },
    {
      title: "Published",
      key: "published_status",
      render: (_: unknown, record: RecipeSummary) => {
        if (!record.is_published) {
          return <Tag>Draft</Tag>;
        }
        return (
          <Space>
            <Tag color="green">Published</Tag>
            {record.prod_enabled && <Tag color="red">Prod</Tag>}
          </Space>
        );
      },
    },
    {
      title: "Last Run",
      dataIndex: "last_run_status",
      key: "last_run_status",
      render: (status: string | undefined) => {
        if (!status) {
          return <Tag>No runs</Tag>;
        }
        const colorMap: Record<string, string> = {
          success: "green",
          failure: "red",
          pending: "orange",
          in_progress: "blue",
        };
        return <Tag color={colorMap[status] || "default"}>{status}</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, record: RecipeSummary) => (
        <Space>
          <Button type="link" onClick={() => history.push(`${url}/${record.recipe_id}`)}>
            Edit
          </Button>
          <Button
            type="link"
            onClick={() => {
              setCloneSourceId(record.recipe_id);
              setCloneNewId("");
              setCloneModalVisible(true);
            }}
          >
            Clone
          </Button>
          {record.is_published && (
            <Button
              type="link"
              danger={record.prod_enabled}
              onClick={() => {
                setPromoteTarget(record);
                setPromoteModalVisible(true);
              }}
            >
              {record.prod_enabled ? "Demote from Prod" : "Promote to Prod"}
            </Button>
          )}
          {record.is_published && (
            <Button type="link" danger onClick={() => handleUnpublish(record)}>
              Unpublish
            </Button>
          )}
          {!record.is_published && record.last_run_status === "success" && (
            <Button
              type="link"
              loading={publishingId === record.recipe_id}
              onClick={() => handlePublish(record)}
            >
              Publish
            </Button>
          )}
          {!record.is_published && (
            <Button
              type="link"
              danger
              onClick={() => {
                setDeleteTarget(record);
                setDeleteModalVisible(true);
              }}
            >
              Delete
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Title level={3}>{source} Recipes</Title>
        <Button type="primary" onClick={() => setCreateModalVisible(true)}>
          Create New Recipe
        </Button>
      </div>

      <Table
        dataSource={recipes}
        columns={columns}
        rowKey="recipe_id"
        loading={loading}
        style={{ marginTop: 16 }}
      />

      {/* Create Modal */}
      <Modal
        title="Create New Recipe"
        open={createModalVisible}
        onOk={handleCreate}
        onCancel={() => {
          setCreateModalVisible(false);
          setNewRecipeId("");
        }}
        okText="Create"
      >
        <div style={{ marginBottom: 8 }}>
          Enter a unique recipe ID (lowercase, numbers, underscores only):
        </div>
        <Input
          placeholder="e.g. post_reactions"
          value={newRecipeId}
          onChange={e => setNewRecipeId(e.target.value)}
          onPressEnter={handleCreate}
        />
      </Modal>

      {/* Clone Modal (Fix 4) */}
      <Modal
        title={`Clone Recipe: ${cloneSourceId}`}
        open={cloneModalVisible}
        onOk={handleClone}
        onCancel={() => {
          setCloneModalVisible(false);
          setCloneNewId("");
        }}
        okText="Clone"
      >
        <div style={{ marginBottom: 8 }}>
          Enter a new recipe ID for the clone (lowercase, numbers, underscores only):
        </div>
        <Input
          placeholder="e.g. post_reactions_v2"
          value={cloneNewId}
          onChange={e => setCloneNewId(e.target.value)}
          onPressEnter={handleClone}
        />
      </Modal>

      {/* Delete Modal */}
      <Modal
        title="Delete Recipe"
        open={deleteModalVisible}
        onOk={handleDelete}
        onCancel={() => {
          setDeleteModalVisible(false);
          setDeleteTarget(null);
        }}
        confirmLoading={deleting}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        <Alert
          type="error"
          showIcon
          message="This will permanently delete the recipe workspace"
          description={`Recipe "${deleteTarget?.recipe_id}" and all its run history will be permanently deleted. This cannot be undone.`}
          style={{ marginBottom: 16 }}
        />
      </Modal>

      {/* Promote/Demote Modal */}
      <Modal
        title={promoteTarget?.prod_enabled ? "Demote from Production" : "Promote to Production"}
        open={promoteModalVisible}
        onOk={handlePromote}
        onCancel={() => {
          setPromoteModalVisible(false);
          setPromoteTarget(null);
        }}
        confirmLoading={promoting}
        okText={promoteTarget?.prod_enabled ? "Demote" : "Promote to Production"}
        okButtonProps={promoteTarget?.prod_enabled ? undefined : { danger: true }}
      >
        {promoteTarget?.prod_enabled ? (
          <Alert
            type="warning"
            showIcon
            message="This will remove the recipe from production"
            description="Existing imports using this recipe will stop working. Are you sure?"
            style={{ marginBottom: 16 }}
          />
        ) : (
          <Alert
            type="error"
            showIcon
            message="This will make the recipe available to all production users"
            description={`Recipe "${promoteTarget?.recipe_id}" will be live in production immediately.`}
            style={{ marginBottom: 16 }}
          />
        )}
      </Modal>
    </div>
  );
};
